import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@tailoring-crm/ui';

/**
 * The last line of defence. A render that throws must not leave the shop staring at a blank
 * white screen mid-transaction — it shows what happened and offers a way back.
 *
 * Deliberately NOT a place to handle expected failures: a rejected request, a 404 on an order
 * id, a plan limit are all normal outcomes and belong in the screen that made the request.
 * This catches the ones nobody predicted.
 */

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Reported to a monitoring service by task 6; for now it lands in the console. */
  onError?: (error: Error, info: ErrorInfo) => void;
  fallback?: (props: { error: Error; reset: () => void }) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
    console.error('[ui] unhandled render error', error, info.componentStack);
  }

  readonly reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback({ error, reset: this.reset });
    return <ErrorFallback error={error} reset={this.reset} />;
  }
}

export function ErrorFallback({ error, reset }: { error: Error; reset: () => void }) {
  const { t } = useTranslation();

  return (
    <div
      role="alert"
      className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center gap-4 px-6 text-center"
    >
      <span className="flex size-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
        <AlertTriangle aria-hidden="true" className="size-6" />
      </span>
      <div className="flex flex-col gap-1">
        <h1 className="text-lg font-semibold">{t('error.boundary.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('error.boundary.description')}</p>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button onClick={reset}>{t('error.boundary.retry')}</Button>
        <Button
          variant="outline"
          onClick={() => {
            window.location.assign('/');
          }}
        >
          {t('error.boundary.goHome')}
        </Button>
      </div>
      {/* The technical detail is available but never the headline — it means nothing to a shop owner. */}
      <details className="w-full text-left">
        <summary className="cursor-pointer text-xs text-muted-foreground">
          {t('error.boundary.details')}
        </summary>
        <pre className="mt-2 overflow-x-auto rounded-md bg-muted p-3 text-xs text-muted-foreground">
          {error.message}
        </pre>
      </details>
    </div>
  );
}
